/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import AllCouponsContext from "../../../context/allCoupons/AllCouponsContext";
import AddCouponContext from "../../../context/addCoupon/AddCouponContext";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={0} ref={ref} variant="filled" {...props} />;
});

const AddCoupon = ({ hideModal, handleSetCoupon, action, onDelete }) => {
  const allCouponsContext = useContext(AllCouponsContext);
  const couponContext = useContext(AddCouponContext);
  const allCouponsArr = allCouponsContext.state || [];

  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkDuplicateEntry = (code) => {
    if (!code) {
      setErrorMsg("");
      return;
    }
    const cleanCode = code.trim().toUpperCase();
    const currentId = couponContext.state.id;
    const chk = allCouponsArr.filter(
      (c) => c.couponCode === cleanCode && (action === "ADD" || c.id !== currentId)
    );
    if (chk.length > 0) {
      setErrorMsg("This coupon code already exists. Please choose a unique code.");
    } else {
      setErrorMsg("");
    }
  };

  useEffect(() => {
    if (action === "ADD" && couponContext.state.couponCode) {
      checkDuplicateEntry(couponContext.state.couponCode);
    }
  }, []);

  const handleCodeChange = (e) => {
    const rawVal = e.target.value.replace(/\s+/g, "_").toUpperCase();
    checkDuplicateEntry(rawVal);
    couponContext.setState({
      ...couponContext.state,
      couponCode: rawVal,
      id: rawVal,
    });
  };

  const handleNumericChange = (field, val) => {
    if (val === "" || /^\d*$/.test(val)) {
      couponContext.setState({
        ...couponContext.state,
        [field]: val === "" ? "" : Number(val),
      });
    }
  };

  const handleSubmit = async () => {
    if (
      errorMsg ||
      !couponContext.state.couponCode ||
      Number(couponContext.state.amount) < 1 ||
      couponContext.state.minShopping === "" ||
      Number(couponContext.state.maxUses) < 1
    ) {
      return;
    }
    setIsSubmitting(true);
    try {
      await handleSetCoupon(action);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    !errorMsg &&
    couponContext.state.couponCode?.trim().length >= 2 &&
    Number(couponContext.state.amount) > 0 &&
    couponContext.state.minShopping !== "" &&
    Number(couponContext.state.minShopping) >= 0 &&
    Number(couponContext.state.maxUses) > 0;

  const currentCode = couponContext.state.couponCode || "PROMOCODE";
  const currentAmount = couponContext.state.amount || 0;
  const currentMin = couponContext.state.minShopping || 0;
  const currentUses = couponContext.state.maxUses || 0;
  const currentIsActive = couponContext.state.isActive !== false;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "90vh",
        bgcolor: "#ffffff",
      }}
    >
      {/* 1. Modal Header */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.2,
          borderBottom: "1px solid #f1f5f9",
          bgcolor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              bgcolor: "rgba(0, 77, 64, 0.08)",
              color: "#004d40",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalOfferOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "1.1rem" }}>
              {action === "ADD" ? "Create New Coupon" : `Edit Coupon #${couponContext.state.couponCode}`}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {action === "ADD"
                ? "Define discount vouchers, cart thresholds, and customer redemption limits."
                : "Update discount parameters and active storefront status."}
            </Typography>
          </Box>
        </Stack>

        <IconButton
          onClick={hideModal}
          size="small"
          sx={{
            color: "#64748b",
            bgcolor: "#f8fafc",
            "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* 2. Scrollable Body */}
      <Box sx={{ p: { xs: 2.5, sm: 3 }, overflowY: "auto", flex: 1, bgcolor: "#f8fafc" }}>
        <Stack spacing={2.5}>
          {/* Validation Alert */}
          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                bgcolor: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                py: 0.5,
              }}
            >
              {errorMsg}
            </Alert>
          )}

          {/* Interactive Live Voucher Preview Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
              color: "#ffffff",
              boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background Ticket Watermark */}
            <ConfirmationNumberOutlinedIcon
              sx={{
                position: "absolute",
                right: -20,
                bottom: -20,
                fontSize: 160,
                color: "rgba(255, 255, 255, 0.06)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box>
                <Chip
                  label={currentIsActive ? "LIVE ON STORE" : "INACTIVE / PAUSED"}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    bgcolor: currentIsActive ? "rgba(16, 185, 129, 0.2)" : "rgba(255, 255, 255, 0.15)",
                    color: currentIsActive ? "#a7f3d0" : "#ffffff",
                    border: `1px solid ${currentIsActive ? "#34d399" : "rgba(255,255,255,0.3)"}`,
                    mb: 1,
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
                  ₹{Number(currentAmount).toLocaleString("en-IN")} OFF
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  border: "1px dashed rgba(255, 255, 255, 0.5)",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.65rem", display: "block" }}>
                  CODE
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    fontFamily: "ui-monospace, monospace",
                    letterSpacing: "0.08em",
                  }}
                >
                  {currentCode}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.15)", my: 1.5 }} />

            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="caption" sx={{ opacity: 0.88 }}>
                🛒 Min. Order: <strong>₹{Number(currentMin).toLocaleString("en-IN")}</strong>
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.88 }}>
                👥 Usage Limit: <strong>{currentUses || 0} times</strong>
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.88 }}>
                🎉 Redeemed: <strong>{couponContext.state.users?.length || 0} users</strong>
              </Typography>
            </Stack>
          </Paper>

          {/* Form Fields Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 2 }}>
              Coupon Configuration
            </Typography>

            <Grid container spacing={2.5}>
              {/* Coupon Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Coupon Code *"
                  placeholder="e.g. AYURVEDA50"
                  value={couponContext.state.couponCode || ""}
                  onChange={handleCodeChange}
                  disabled={action === "EDIT"}
                  error={Boolean(errorMsg)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "10px",
                      bgcolor: action === "EDIT" ? "#f1f5f9" : "#f8fafc",
                      fontFamily: "ui-monospace, monospace",
                      fontWeight: 700,
                    },
                  }}
                  helperText={action === "EDIT" ? "Coupon code cannot be changed once created." : "Uppercase letters & numbers."}
                />
              </Grid>

              {/* Discount Amount */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Discount Amount (₹) *"
                  placeholder="e.g. 100"
                  value={couponContext.state.amount === "" ? "" : couponContext.state.amount}
                  onChange={(e) => handleNumericChange("amount", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CurrencyRupeeRoundedIcon sx={{ fontSize: 18, color: "#004d40" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "10px", bgcolor: "#f8fafc", fontWeight: 700 },
                  }}
                  helperText="Flat rupee discount deducted from cart total."
                />
              </Grid>

              {/* Min Shopping */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Minimum Cart Value (₹) *"
                  placeholder="e.g. 499"
                  value={couponContext.state.minShopping === "" ? "" : couponContext.state.minShopping}
                  onChange={(e) => handleNumericChange("minShopping", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "10px", bgcolor: "#f8fafc" },
                  }}
                  helperText="Minimum order value required to apply."
                />
              </Grid>

              {/* Max Uses */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Maximum Redemptions *"
                  placeholder="e.g. 500"
                  value={couponContext.state.maxUses === "" ? "" : couponContext.state.maxUses}
                  onChange={(e) => handleNumericChange("maxUses", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GroupOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "10px", bgcolor: "#f8fafc" },
                  }}
                  helperText="Global usage cap across all customers."
                />
              </Grid>

              {/* Active Toggle Switch */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                    border: "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      Coupon Availability Status
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {currentIsActive
                        ? "Active — customers can apply this code during checkout."
                        : "Inactive — code cannot be redeemed by customers."}
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentIsActive}
                        onChange={(e) =>
                          couponContext.setState({
                            ...couponContext.state,
                            isActive: e.target.checked,
                          })
                        }
                        color="secondary"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      </Box>

      {/* 3. Modal Footer */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid #f1f5f9",
          bgcolor: "#ffffff",
          display: "flex",
          justifyContent: action === "EDIT" && onDelete ? "space-between" : "flex-end",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {action === "EDIT" && onDelete && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => {
              onDelete(couponContext.state);
            }}
            disabled={isSubmitting}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              borderColor: "#fca5a5",
              color: "#dc2626",
              "&:hover": { borderColor: "#ef4444", bgcolor: "#fef2f2" },
            }}
          >
            Delete Coupon
          </Button>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={hideModal}
            disabled={isSubmitting}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              borderColor: "#cbd5e1",
              color: "#475569",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#004d40",
              px: 3,
              py: 0.9,
              fontSize: "0.875rem",
              boxShadow: "0 4px 12px rgba(0, 77, 64, 0.2)",
              "&:hover": { bgcolor: "#00382e" },
            }}
          >
            {isSubmitting
              ? "Saving..."
              : action === "ADD"
              ? "Create Coupon"
              : "Update Coupon"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddCoupon;
